import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Mail, RefreshCw, Eye, MousePointerClick, CheckCircle2, XCircle, AlertTriangle, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

const STATUS_STYLES = {
  sent:         { label: 'Sent',       icon: Send,           className: 'bg-slate-100 text-slate-700 border-slate-200' },
  delivered:    { label: 'Delivered',  icon: CheckCircle2,   className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  opened:       { label: 'Opened',     icon: Eye,            className: 'bg-sky-50 text-sky-700 border-sky-200' },
  clicked:      { label: 'Clicked',    icon: MousePointerClick, className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  bounced:      { label: 'Bounced',    icon: XCircle,        className: 'bg-rose-50 text-rose-700 border-rose-200' },
  complained:   { label: 'Complained', icon: AlertTriangle,  className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

/**
 * Sent-history timeline for Scope Pack email sends. Polls on mount + every
 * 20s while mounted so opens/clicks surface without a manual refresh.
 */
export function ScopePackSendHistory({ estimateId, refreshKey = 0 }) {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!estimateId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/scope-pack/sends`, {
        params: { estimate_id: estimateId, limit: 50 },
        withCredentials: true,
      });
      setSends(res.data?.sends || []);
    } catch {
      // silent — panel just stays empty
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!estimateId) return undefined;
    const h = setInterval(load, 20000);
    return () => clearInterval(h);
  }, [estimateId, load]);

  if (!estimateId) return null;
  if (sends.length === 0 && !loading) return null; // hide until first send

  return (
    <Card className="border-slate-200" data-testid="scope-pack-send-history">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-[#0F2040] flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Scope Pack — Sent History
          <Badge variant="secondary" className="ml-2 text-[10px] bg-slate-100 text-slate-600">
            {sends.length}
          </Badge>
          <Button type="button" variant="ghost" size="sm" onClick={load}
            className="ml-auto h-6 px-2 text-[10px] text-slate-500 hover:text-[#0F2040]"
            data-testid="scope-pack-history-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="divide-y divide-slate-100">
          {sends.map(s => {
            const meta = STATUS_STYLES[s.status] || STATUS_STYLES.sent;
            const Icon = meta.icon;
            return (
              <div key={s.send_id} className="py-2 flex items-center gap-3 text-xs"
                data-testid={`send-row-${s.send_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-800 truncate">
                    {s.recipient_name ? `${s.recipient_name} ` : ''}
                    <span className="text-slate-500">&lt;{s.recipient_email}&gt;</span>
                  </div>
                  <div className="text-[10px] text-slate-400 truncate">
                    by {s.sent_by_user_name || s.sent_by_user_email} &middot; {formatRelative(s.sent_at)}
                  </div>
                </div>
                <Badge variant="outline" className={`${meta.className} gap-1 px-2 py-0.5 text-[10px] font-medium`}>
                  <Icon className="w-3 h-3" />
                  {meta.label}
                </Badge>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
